import { useCallback, useState } from 'react';

import { type ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Button } from '@mui/material';
import { X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PASS_LEAD_MUTATION } from '@modules/_shared/components/feed/Activity/Assignment';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { PassLeadDialog } from '@modules/_shared/components/PassLeadDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { GET_DASHBOARD_NEW_LEADS } from '@modules/dashboard/dashboardQueries';
import { GET_LEADS, LEAD_FEED } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

type PassLeadButtonProps = {
  leadId: string | null;
  passForUserId: string | null;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  isInLeadDrawer?: boolean;
  onPass?: () => void;
};

export const PassLeadButton = ({
  leadId,
  passForUserId,
  size,
  disabled = false,
  isInLeadDrawer = false,
  onPass,
}: PassLeadButtonProps) => {
  const { t } = useLocale();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { me } = useAppData();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = me?.is_admin ?? false;

  const [passLead, { loading: isPassing }] = useMutation(PASS_LEAD_MUTATION, {
    onError: setErrorLike,
    onCompleted: () => {
      emitFeedRefresh();
      onPass?.();

      if (!isAdmin && isInLeadDrawer) {
        navigate({
          pathname: '..',
          search: location.search,
        });
      }
    },
    refetchQueries: [GET_LEADS, GET_DASHBOARD_NEW_LEADS, LEAD_FEED],
  });

  const handlePassClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDialogOpen(true);
  }, []);

  const handleReasonSelect = useCallback(
    async (note: string) => {
      setDialogOpen(false);

      if (leadId == null || passForUserId == null) {
        return;
      }

      await passLead({
        variables: {
          lead_id: leadId,
          user_id: passForUserId,
          note,
        },
      });
    },
    [passLead, leadId, passForUserId],
  );

  return (
    <>
      <Button
        variant="soft"
        size={size}
        color="error"
        startIcon={
          <X size={size === 'large' ? 18 : size === 'small' ? 14 : 16} />
        }
        onClick={handlePassClick}
        loading={isPassing}
        loadingPosition="start"
        disabled={disabled || !leadId || !passForUserId}
      >
        {t('Pass')}
      </Button>

      <PassLeadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onReasonSelect={handleReasonSelect}
        loading={isPassing}
      />

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
